import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const TermsPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      {/* Header */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0f]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </button>
        </div>
      </nav>

      {/* Content */}
      <div className="pt-24 pb-16 px-6">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-4xl font-bold font-['Unbounded'] mb-2">Terms of Service</h1>
          <p className="text-gray-500 mb-12">Last updated: April 2026</p>

          <div className="space-y-8 text-gray-300 leading-relaxed">
            <section>
              <h2 className="text-xl font-semibold text-white mb-4">1. Acceptance of Terms</h2>
              <p>
                By accessing or using EleKtro Universal ("the Platform"), you agree to be bound by these 
                Terms of Service. If you do not agree to these terms, do not use the Platform.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">2. Platform Overview</h2>
              <p>
                EleKtro Universal is a performance-based marketing platform that connects businesses 
                with creators. Businesses pay for real results (clicks, leads, customers) delivered 
                by creators through tracked campaigns.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">3. Business Accounts</h2>
              <p className="mb-4">As a business user, you agree to:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-400">
                <li>Pay the monthly subscription fee (€99/month)</li>
                <li>Fund campaign budgets for creator payouts</li>
                <li>Provide accurate campaign information</li>
                <li>Not engage in misleading or fraudulent campaigns</li>
                <li>Respect creator relationships and communications</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">4. Creator Accounts</h2>
              <p className="mb-4">As a creator, you agree to:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-400">
                <li>Provide accurate profile and audience information</li>
                <li>Promote campaigns honestly and authentically</li>
                <li>Not use fraudulent methods to generate clicks or conversions</li>
                <li>Comply with platform guidelines for content promotion</li>
                <li>Disclose sponsored content as required by law</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">5. Payment Terms</h2>
              <p className="mb-4">
                <strong className="text-white">For Businesses:</strong> Subscription fees are billed monthly. 
                Campaign budgets are charged separately when added. All payments are processed via Stripe.
              </p>
              <p className="mb-4">
                <strong className="text-white">For Creators:</strong> Earnings are based on verified results. 
                Creators receive up to 80% of campaign budgets based on performance. Payouts are processed 
                via Stripe Connect. Minimum payout threshold is €10.
              </p>
              <p>
                <strong className="text-white">Performance Verification:</strong> Results are tracked and 
                verified through our platform. We reserve the right to investigate and adjust payouts 
                for suspicious or fraudulent activity.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">6. Platform Integrity</h2>
              <p className="mb-4">
                We reserve the right to suspend or terminate accounts that violate our terms, misuse 
                the platform, or negatively impact other users.
              </p>
              <p>
                This includes fraud, misleading promotion, abuse, or behavior that harms the platform 
                or its integrity. Decisions regarding account status are made at our sole discretion.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">7. Intellectual Property</h2>
              <p>
                You retain ownership of content you create. By using the Platform, you grant us a 
                license to display your content for platform operations. You may not use our 
                trademarks, logos, or branding without permission.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">8. Limitation of Liability</h2>
              <p>
                The Platform is provided "as is" without warranties of any kind. We are not liable for 
                indirect, incidental, or consequential damages arising from your use of the Platform. 
                Our total liability is limited to the amount you paid us in the past 12 months.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">9. Disclaimer</h2>
              <p>
                We do not guarantee specific results from campaigns. Success depends on many factors 
                including campaign quality, creator selection, and market conditions. We are not 
                responsible for the actions or content of third-party users.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">10. Modifications</h2>
              <p>
                We may modify these terms at any time. Continued use of the Platform after changes 
                constitutes acceptance of the new terms. We will notify users of significant changes 
                via email or platform notification.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">11. Governing Law</h2>
              <p>
                These terms are governed by the laws of Denmark. Any disputes will be resolved in 
                the courts of Denmark.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">12. Contact</h2>
              <p>
                For questions about these Terms of Service, contact us at legal@elektrouniversal.dk
              </p>
            </section>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-white/5 py-8 px-6">
        <div className="max-w-4xl mx-auto text-center text-sm text-gray-600">
          © 2026 EleKtro Universal. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default TermsPage;
